import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { DashboardAPI } from "@/lib/dashboard-api";
import { NoticesAPI } from "@/lib/sprint7-api";
import { TenantsAPI } from "@/lib/sprint2-api";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Calendar, Megaphone, GraduationCap, UserCog, FileText, CalendarCheck, Wallet,
  ClipboardList, Library, BookOpen, BarChart3, Settings2, Loader2, ChevronLeft, ChevronRight,
} from "lucide-react";

import { SectionsAPI } from "@/lib/sprint2-api";

export default function Dashboard() {
  const { user } = useAuth();
  const isSuper = (user?.roles || []).includes("super_admin");
  const isClassTeacher = (user?.roles || []).includes("class_teacher");
  const tQ = useQuery({
    queryKey: ["dashboard-tenants"],
    queryFn: () => TenantsAPI.list(),
    enabled: isSuper,
  });
  const dashTenantId = isSuper ? tQ.data?.[0]?.id : user?.tenant_id;
  const configQ = useQuery({ queryKey: ["dashboard-config"], queryFn: DashboardAPI.config });
  const statsQ = useQuery({
    queryKey: ["dashboard-stats", dashTenantId],
    queryFn: () => DashboardAPI.stats(dashTenantId ? { tenant_id: dashTenantId } : {}),
    enabled: !!dashTenantId,
  });
  // Fetch the sections where this user is the Class Teacher — powers the
  // "My Class" hero card.
  const myClassesQ = useQuery({
    queryKey: ["my-class-sections"],
    queryFn: () => SectionsAPI.myClasses(),
    enabled: isClassTeacher,
  });
  const widgets = configQ.data?.widgets || ["calendar", "notices"];
  const canCustomize = !!configQ.data?.can_customize;

  return (
    <AppShell>
      <div className="px-6 py-5" data-testid="dashboard-page">
        <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
          <div>
            <h1 className="font-display font-bold tracking-tight text-2xl">
              Good to see you, {(() => {
                const full = (user?.name || "").trim();
                if (!full) return "there";
                // Prefer the first token unless it looks like an acronym (all-caps ≤3 chars) —
                // in that case, show the full display name so users like "US Academy Admin"
                // don't get truncated to "US.".
                const first = full.split(/\s+/)[0];
                return /^[A-Z]{1,3}$/.test(first) ? full : first;
              })()}.
            </h1>
            {canCustomize && configQ.data && (
              <p className="text-[12px] text-muted-foreground/80 mt-0.5">
                Drag any module from the sidebar into your dashboard via Customize.
              </p>
            )}
          </div>
          {canCustomize && configQ.data && (
            <CustomizeDialog
              available={configQ.data.available}
              alwaysOn={configQ.data.always_on || []}
              current={widgets}
            />
          )}
        </div>

        <div className="grid grid-cols-12 gap-4" data-testid="dashboard-grid">
          {isClassTeacher && (myClassesQ.data || []).length > 0 && (
            <section className="col-span-12" data-testid="my-class-hero">
              <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-5 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-indigo-700 flex items-center gap-1.5 mb-1">
                    <UserCog className="h-3 w-3" /> My Class
                  </div>
                  <h2 className="font-display font-bold text-xl">
                    You are the Class Teacher of{" "}
                    <span className="text-indigo-700">
                      {(myClassesQ.data || []).map((s) => `Section ${s.name}`).join(", ")}
                    </span>
                  </h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    Quick access — mark today&apos;s attendance, publish homework or drop a notice for your students.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="my-class-actions">
                  <Link to="/attendance" className="text-xs font-semibold px-3.5 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition" data-testid="my-class-attendance">
                    Mark attendance
                  </Link>
                  <Link to="/homework" className="text-xs font-semibold px-3.5 py-2 rounded-md border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 transition" data-testid="my-class-homework">
                    Post homework
                  </Link>
                  <Link to="/notices" className="text-xs font-semibold px-3.5 py-2 rounded-md border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 transition" data-testid="my-class-notice">
                    Write notice
                  </Link>
                </div>
              </div>
            </section>
          )}
          {widgets.map((key) => (
            <WidgetWrapper key={key} widgetKey={key}>
              <Widget widgetKey={key} stats={statsQ.data} />
            </WidgetWrapper>
          ))}
        </div>
      </div>
    </AppShell>
  );
}

function WidgetWrapper({ widgetKey, children }) {
  // Full-width for calendar + notices + roadmap; half-width for KPIs
  const big = ["calendar", "notices", "roadmap"].includes(widgetKey);
  return (
    <section className={big ? "col-span-12 lg:col-span-6" : "col-span-12 sm:col-span-6 lg:col-span-3"} data-testid={`widget-${widgetKey}`}>
      {children}
    </section>
  );
}

function Widget({ widgetKey, stats }) {
  switch (widgetKey) {
    case "calendar":
      return <CalendarWidget />;
    case "notices":
      return <NoticesWidget />;
    case "kpi-students":
      return <KpiCard icon={GraduationCap} label="Active students" value={stats?.students ?? "—"} link="/students" tone="students" />;
    case "kpi-staff":
      return <KpiCard icon={UserCog} label="Active staff" value={stats?.staff ?? "—"} link="/staff" tone="staff" />;
    case "kpi-admissions":
      return <AdmissionsKpi funnel={stats?.admissions} />;
    case "kpi-attendance":
      return <AttendanceKpi today={stats?.attendance_today} />;
    case "kpi-fees":
      return <FeesKpi fees={stats?.fees_this_month} />;
    case "kpi-homework":
      return <KpiCard icon={ClipboardList} label="Homework due this week" value={stats?.homework_open ?? "—"} link="/homework" tone="homework" />;
    case "kpi-library":
      return <LibraryKpi loans={stats} />;
    case "kpi-curriculum":
      return <KpiCard icon={BookOpen} label="Lessons in curriculum" value={stats?.curriculum?.lessons ?? "—"} sub={`${stats?.curriculum?.units ?? 0} units`} link="/curriculum" tone="curriculum" />;
    case "roadmap":
      return <RoadmapWidget />;
    default:
      return null;
  }
}

// =========== Calendar widget ===========
function CalendarWidget() {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const { user } = useAuth();
  const isSuper = (user?.roles || []).includes("super_admin");
  const tQ = useQuery({
    queryKey: ["calendar-tenants"],
    queryFn: () => TenantsAPI.list(),
    enabled: isSuper,
  });
  const tenantId = isSuper ? tQ.data?.[0]?.id : user?.tenant_id;
  const boardQ = useQuery({
    queryKey: ["notices-calendar", tenantId],
    queryFn: () => NoticesAPI.board(tenantId ? { tenant_id: tenantId, limit: 200 } : { limit: 200 }),
    enabled: !!tenantId,
  });

  // Map published_at date → notice count
  const eventDays = {};
  for (const n of (boardQ.data || [])) {
    if (!n.published_at) continue;
    try {
      const d = new Date(n.published_at);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      eventDays[key] = (eventDays[key] || 0) + 1;
    } catch { /* noop */ }
  }

  const month = cursor.getMonth();
  const year = cursor.getFullYear();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const stepMonth = (delta) => {
    const next = new Date(year, month + delta, 1);
    setCursor(next);
  };

  return (
    <div className="bg-card rounded-2xl shadow-sm p-5" data-testid="calendar-widget">
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">Calendar</span>
        </div>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" onClick={() => stepMonth(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="text-sm font-display font-semibold min-w-[140px] text-center">{monthLabel}</div>
          <Button size="icon" variant="ghost" onClick={() => stepMonth(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </header>
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-muted-foreground mb-1 text-center">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;
          const key = `${year}-${month}-${d}`;
          const count = eventDays[key];
          return (
            <div
              key={i}
              className={`relative h-10 rounded-md flex items-center justify-center text-sm border ${isToday ? "bg-primary text-primary-foreground border-primary font-semibold" : "border-border bg-background"}`}
              data-testid={count ? `cal-day-${key}` : undefined}
            >
              {d}
              {count > 0 && (
                <span className={`absolute bottom-0.5 h-1 w-1 rounded-full ${isToday ? "bg-primary-foreground" : "bg-primary"}`} title={`${count} notice${count === 1 ? "" : "s"} published`} />
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-2 text-[10px] text-muted-foreground">
        Dots mark days with published notices.
      </div>
    </div>
  );
}

// =========== Notices widget ===========
function NoticesWidget() {
  const { user } = useAuth();
  const isSuper = (user?.roles || []).includes("super_admin");
  const tQ = useQuery({
    queryKey: ["notices-dash-tenants"],
    queryFn: () => TenantsAPI.list(),
    enabled: isSuper,
  });
  const tenantId = isSuper ? tQ.data?.[0]?.id : user?.tenant_id;
  const boardQ = useQuery({
    queryKey: ["notices-dash", tenantId],
    queryFn: () => NoticesAPI.board(tenantId ? { tenant_id: tenantId, limit: 50 } : { limit: 50 }),
    enabled: !!tenantId,
  });
  const [selected, setSelected] = useState(null);
  // "Valid till current date" → published within last 30 days (admins keep history short).
  const rows = useMemo(() => {
    const all = boardQ.data || [];
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return all.filter((n) => new Date(n.published_at).getTime() >= cutoff).slice(0, 5);
  }, [boardQ.data]);
  return (
    <div className="bg-card rounded-2xl shadow-sm p-5 h-full flex flex-col" data-testid="notices-widget">
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">Notices</span>
          {rows.length > 0 && <Badge className="text-[10px] h-4 px-1.5">{rows.length}</Badge>}
        </div>
        <Link to="/notices" className="text-xs text-primary hover:underline">View all →</Link>
      </header>
      {boardQ.isLoading ? (
        <div className="py-8 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="py-10 flex flex-col items-center text-center gap-2">
          <div className="h-12 w-12 rounded-full bg-secondary/50 flex items-center justify-center">
            <Megaphone className="h-5 w-5 text-muted-foreground/60" />
          </div>
          <div className="text-[12px] text-muted-foreground/80">No notices yet</div>
          <Link to="/notices/create" className="text-[11px] text-primary hover:underline">Compose the first one →</Link>
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((n) => {
            const firstImg = (n.attachments || []).find((a) => (a.content_type || "").startsWith("image/"));
            return (
              <li key={n.id} data-testid={`notice-mini-${n.id}`}>
                <button
                  type="button"
                  onClick={() => setSelected(n)}
                  className="w-full py-2.5 flex items-start gap-2.5 text-left hover:bg-muted/40 transition rounded px-1 -mx-1"
                  data-testid={`notice-open-${n.id}`}
                >
                  {firstImg ? (
                    <img src={NoticesAPI.attachmentUrl(firstImg.id)} alt={firstImg.filename} className="h-10 w-10 rounded object-cover border border-border shrink-0" />
                  ) : (
                    <div className="h-10 w-10 rounded bg-muted flex items-center justify-center shrink-0">
                      <Megaphone className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{n.title}</div>
                    <div className="text-[11px] text-primary truncate">{n.audience_label}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{fmtTime(n.published_at)}</div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Notice detail modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="notice-modal">
          <DialogHeader>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="capitalize">{selected?.notice_type || "general"}</Badge>
              <DialogTitle className="text-2xl font-display">{selected?.title}</DialogTitle>
            </div>
            <DialogDescription>
              <span className="text-primary font-medium">{selected?.audience_label}</span>
              <span className="block text-xs mt-1">{selected?.author_email} · {fmtTime(selected?.published_at)} · {selected?.recipients} recipients</span>
            </DialogDescription>
          </DialogHeader>
          {selected?.body && (
            <div className="prose prose-sm max-w-none text-sm leading-relaxed [&>p]:my-1" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selected.body, { ALLOWED_TAGS: ["p","br","strong","em","u","s","a","ul","ol","li","blockquote","h1","h2","h3","h4","h5","h6","img","code","pre","hr","span","div"], ALLOWED_ATTR: ["href","src","alt","title","target","rel","class"] }) }} />
          )}
          {(selected?.attachments || []).filter((a) => (a.content_type || "").startsWith("image/")).length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
              {selected.attachments.filter((a) => (a.content_type || "").startsWith("image/")).map((a) => (
                <a key={a.id} href={NoticesAPI.attachmentUrl(a.id)} target="_blank" rel="noreferrer">
                  <img src={NoticesAPI.attachmentUrl(a.id)} alt={a.filename} className="w-full max-h-[260px] object-cover rounded border border-border" />
                </a>
              ))}
            </div>
          )}
          {(selected?.attachments || []).filter((a) => !(a.content_type || "").startsWith("image/")).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {selected.attachments.filter((a) => !(a.content_type || "").startsWith("image/")).map((a) => (
                <a key={a.id} href={NoticesAPI.attachmentUrl(a.id)} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 border border-border rounded bg-muted/30 hover:bg-muted">
                  📎 {a.filename}
                </a>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)} data-testid="notice-modal-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========== KPI helpers ===========
// Color palette per KPI key — matches the friendly card style in the design reference.
const KPI_TONE = {
  default: { tint: "bg-primary/10 text-primary" },
  students: { tint: "bg-sky-500/10 text-sky-600" },
  staff: { tint: "bg-violet-500/10 text-violet-600" },
  admissions: { tint: "bg-amber-500/10 text-amber-600" },
  attendance: { tint: "bg-emerald-500/10 text-emerald-600" },
  fees: { tint: "bg-rose-500/10 text-rose-600" },
  homework: { tint: "bg-indigo-500/10 text-indigo-600" },
  library: { tint: "bg-fuchsia-500/10 text-fuchsia-600" },
  curriculum: { tint: "bg-orange-500/10 text-orange-600" },
};

function KpiCard({ icon: Icon, label, value, sub, link, tone = "default" }) {
  const t = KPI_TONE[tone] || KPI_TONE.default;
  const inner = (
    <div className="bg-card rounded-2xl shadow-sm p-5 hover:shadow-md hover:-translate-y-0.5 transition-all h-full flex items-center gap-4">
      <div className={`h-14 w-14 rounded-2xl ${t.tint} flex items-center justify-center shrink-0`}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">{label}</div>
        <div className="font-display font-extrabold text-3xl tracking-tight mt-0.5 leading-none">{value}</div>
        {sub && <div className="text-[11px] text-muted-foreground mt-1.5 truncate">{sub}</div>}
      </div>
    </div>
  );
  return link ? <Link to={link} className="block h-full">{inner}</Link> : inner;
}

function AdmissionsKpi({ funnel }) {
  const open = (funnel?.applied || 0) + (funnel?.shortlisted || 0);
  return <KpiCard icon={FileText} label="Open admissions" value={open} sub={`${funnel?.admitted || 0} admitted`} link="/admissions" tone="admissions" />;
}

function AttendanceKpi({ today }) {
  const present = today?.present || 0;
  const total = present + (today?.absent || 0) + (today?.late || 0) + (today?.excused || 0);
  const pct = total ? Math.round((present / total) * 100) : null;
  return <KpiCard icon={CalendarCheck} label="Attendance today" value={pct == null ? "—" : `${pct}%`} sub={`${present}/${total} present`} link="/attendance" tone="attendance" />;
}

function FeesKpi({ fees }) {
  const paid = fees?.paid || 0;
  const total = fees?.total || 0;
  return <KpiCard icon={Wallet} label="Fees this month" value={`₹${(paid / 1000).toFixed(1)}k`} sub={total ? `of ₹${(total / 1000).toFixed(1)}k billed` : "no invoices"} link="/fees" tone="fees" />;
}

function LibraryKpi({ loans }) {
  const active = loans?.library_loans_active || 0;
  const overdue = loans?.library_loans_overdue || 0;
  return <KpiCard icon={Library} label="Active loans" value={active} sub={overdue ? `${overdue} overdue` : "0 overdue"} link="/library" tone="library" />;
}

// =========== Roadmap (legacy widget kept) ===========
function RoadmapWidget() {
  const sprints = [
    { id: 1, name: "Foundation + Auth", status: "done" },
    { id: 2, name: "Tenants + Institutions", status: "done" },
    { id: 3, name: "Users & RBAC + Bulk Import", status: "done" },
    { id: 4, name: "Admissions", status: "done" },
    { id: 5, name: "Attendance v1", status: "done" },
    { id: 6, name: "Fees + Razorpay", status: "done" },
    { id: 7, name: "Notices + Chat", status: "done" },
    { id: 8, name: "Homework", status: "done" },
    { id: 9, name: "Library (MVP GA)", status: "done" },
    { id: 10, name: "Curriculum", status: "done" },
  ];
  return (
    <div className="bg-card rounded-2xl shadow-sm p-5" data-testid="roadmap-widget">
      <header className="flex items-center gap-2 mb-3">
        <BarChart3 className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">Build roadmap</span>
      </header>
      <ul className="divide-y divide-border">
        {sprints.map((s) => (
          <li key={s.id} className="py-1.5 flex items-center justify-between">
            <span className="text-sm">
              <span className="font-mono text-[11px] text-muted-foreground mr-2">S{String(s.id).padStart(2, "0")}</span>
              {s.name}
            </span>
            <Badge variant="outline" className="text-[10px]">{s.status}</Badge>
          </li>
        ))}
      </ul>
    </div>
  );
}

// =========== Customize dialog ===========
function CustomizeDialog({ available, alwaysOn, current }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(new Set(current));
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();

  const toggle = (key) => {
    if (alwaysOn.includes(key)) return;
    const n = new Set(picked);
    if (n.has(key)) n.delete(key);
    else n.add(key);
    setPicked(n);
  };

  const save = async () => {
    setBusy(true);
    try {
      // Drop always-on; backend will re-prepend them on response
      const toSave = Array.from(picked).filter((k) => !alwaysOn.includes(k));
      await DashboardAPI.saveConfig(toSave);
      toast.success("Dashboard updated");
      qc.invalidateQueries({ queryKey: ["dashboard-config"] });
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setPicked(new Set(current)); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="dashboard-customize-btn">
          <Settings2 className="h-4 w-4 mr-1.5" /> Customize
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl" data-testid="dashboard-customize-dialog">
        <DialogHeader>
          <DialogTitle>Customize dashboard</DialogTitle>
          <DialogDescription>Pick the widgets to show. Calendar and Notices are always on.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-auto">
          {available.map((w) => {
            const locked = alwaysOn.includes(w.key);
            const on = picked.has(w.key) || locked;
            return (
              <label
                key={w.key}
                className={`flex items-start gap-3 border border-border rounded-md px-3 py-2 cursor-pointer ${locked ? "bg-secondary/40" : "hover:bg-secondary/40"}`}
                data-testid={`dash-widget-${w.key}`}
              >
                <Checkbox checked={on} disabled={locked} onCheckedChange={() => toggle(w.key)} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {w.label}
                    {locked && <Badge variant="outline" className="text-[10px]">Always on</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground">{w.description}</div>
                </div>
              </label>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="dashboard-customize-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fmtTime(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
}
